import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Store, LayoutDashboard, FolderOpen, Package, ShoppingCart, 
  BarChart3, User, LogOut, Menu, X, ChevronRight, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { sellerAPI } from '@/lib/api';

// Dashboard pages
import DashboardHome from '@/pages/seller/DashboardHome';
import Categories from '@/pages/seller/Categories';
import Products from '@/pages/seller/Products';
import Orders from '@/pages/seller/Orders';
import Statistics from '@/pages/seller/Statistics';
import Profile from '@/pages/seller/Profile';
import Customers from '@/pages/seller/Customers';

const menuItems = [
  { path: '', icon: LayoutDashboard, label: 'Dashboard' },
  { path: 'categories', icon: FolderOpen, label: 'Categories' },
  { path: 'products', icon: Package, label: 'Products' },
  { path: 'orders', icon: ShoppingCart, label: 'Orders' },
  { path: 'customers', icon: Users, label: 'Customers' },
  { path: 'statistics', icon: BarChart3, label: 'Statistics' },
  { path: 'profile', icon: User, label: 'Profile' },
];

export default function SellerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(null);

	useEffect(() => {
	  // AuthContext localStorage se user set kar raha hota hai, tab tak redirect mat karo
	  if (loading) return;

	  if (!user || user.role !== 'seller') {
		navigate('/seller/login', { replace: true });
		return;
	  }

	  fetchProfile();
	}, [user, loading, navigate]);


  const fetchProfile = async () => {
    try {
      const response = await sellerAPI.getProfile();
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/seller/login');
  };

  const currentPath = location.pathname.replace('/seller/dashboard/', '').replace('/seller/dashboard', '');

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border h-16 flex items-center px-4">
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-muted rounded-lg"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <div className="flex items-center gap-2 ml-4">
          <Store className="h-6 w-6 text-primary" />
          <span className="font-heading font-bold">Dashboard</span>
        </div>
      </header>

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-40 h-full w-64 bg-card border-r border-border transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <Store className="h-7 w-7 text-primary" />
          <span className="font-heading text-lg font-bold">My Market Place</span>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const isActive = currentPath === item.path;
            return (
              <Link
                key={item.path}
                to={`/seller/dashboard/${item.path}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path || 'home'}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Shop Link */}
        {profile && (
          <div className="absolute bottom-20 left-4 right-4">
            <div className="bg-muted rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-2">Your Shop Link</p>
              <Link 
                to={`/shop/${profile.shop_slug}`}
                target="_blank"
                className="text-sm text-primary hover:underline flex items-center gap-1"
                data-testid="shop-link"
              >
                /shop/{profile.shop_slug} <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        )}

        {/* Logout */}
        <div className="absolute bottom-4 left-4 right-4">
          <Button 
            variant="outline" 
            className="w-full justify-start gap-3"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 md:p-8">
          <Routes>
            <Route path="" element={<DashboardHome profile={profile} />} />
            <Route path="categories" element={<Categories />} />
            <Route path="products" element={<Products />} />
            <Route path="orders" element={<Orders />} />
            <Route path="customers" element={<Customers />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="profile" element={<Profile profile={profile} onUpdate={fetchProfile} />} />
          </Routes>
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
